import type { AreaFinding, AreaReport } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { applyAreaFindings, reviewAreaReport } from "../api.js";
import { locale } from "../locale.js";
import { actionButton } from "./controls.js";

/**
 * The fact check of an area report, on the administrator's view of it — task
 * 10.42, decided 2026-09-29 in `project-scope.md`.
 *
 * **Nothing changes until the administrator says so.** The check lists what
 * it found — the passage, what is wrong, the fix and the source — each with a
 * tick, on by default for a fix that can be made; *Använd valda rättelser*
 * makes the ticked ones. A finding with no mechanical fix is shown for the
 * administrator to act on by hand, with no tick.
 *
 * **A check takes about a minute**, and the page says so while it waits. One
 * that could not check every part says how many it missed, rather than
 * reading as a clean bill.
 */
export function AreaReview({
  report,
  onChange,
}: {
  report: AreaReport;
  onChange: (report: AreaReport) => void;
}) {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() =>
    defaults(report.review?.findings),
  );

  const review = report.review;

  const check = () => {
    setRunning(true);
    setFailed(null);
    void reviewAreaReport(report.id)
      .then((next) => {
        onChange(next);
        setChosen(defaults(next.review?.findings));
      })
      .catch(() => setFailed(t("areaReview.failed")))
      .finally(() => setRunning(false));
  };

  const apply = () => {
    setApplying(true);
    setFailed(null);
    void applyAreaFindings(report.id, { ids: [...chosen] })
      .then((next) => {
        onChange(next);
        setChosen(new Set());
      })
      .catch(() => setFailed(t("areaReview.applyFailed")))
      .finally(() => setApplying(false));
  };

  const toggle = (id: string, on: boolean) =>
    setChosen((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <section className="border-line space-y-3 rounded-sm border px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg">{t("areaReview.title")}</h2>
        <button type="button" onClick={check} disabled={running} className={actionButton}>
          {running
            ? t("areaReview.running")
            : review === null
              ? t("areaReview.check")
              : t("areaReview.checkAgain")}
        </button>
      </div>
      <p className="text-muted max-w-prose text-sm">
        {running ? t("areaReview.runningLong") : t("areaReview.intro")}
      </p>
      {failed !== null && (
        <p role="alert" className="text-past text-sm">
          {failed}
        </p>
      )}

      {review !== null && !running && (
        <>
          <p className="text-muted text-xs">
            {t("areaReview.byline", {
              model: review.model,
              date: new Date(review.reviewedAt).toLocaleString(locale(), {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              }),
            })}
            {review.failedParts > 0 &&
              ` ${t("areaReview.incomplete", { count: review.failedParts, parts: review.parts })}`}
          </p>
          {review.findings.length === 0 ? (
            <p className="text-sm">{t("areaReview.none")}</p>
          ) : (
            <ul className="divide-line divide-y">
              {review.findings.map((finding) => (
                <Finding
                  key={finding.id}
                  finding={finding}
                  chosen={chosen.has(finding.id)}
                  onToggle={(on) => toggle(finding.id, on)}
                />
              ))}
            </ul>
          )}
          {review.findings.some((finding) => finding.applicable) && (
            <button
              type="button"
              onClick={apply}
              disabled={applying || chosen.size === 0}
              className={actionButton}
            >
              {applying
                ? t("areaReview.applying")
                : t("areaReview.apply", { count: chosen.size })}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function Finding({
  finding,
  chosen,
  onToggle,
}: {
  finding: AreaFinding;
  chosen: boolean;
  onToggle: (on: boolean) => void;
}) {
  const { t } = useTranslation();
  const id = `finding-${finding.id}`;
  return (
    <li className="flex gap-3 py-3 text-sm">
      <div className="w-5 shrink-0 pt-0.5">
        {finding.applicable && (
          <input
            id={id}
            type="checkbox"
            checked={chosen}
            onChange={(event) => onToggle(event.target.checked)}
            className="accent-accent"
            aria-label={t("areaReview.choose", { problem: finding.problem })}
          />
        )}
      </div>
      <div className="min-w-0 space-y-1">
        <p>
          <span className="text-muted">{finding.part}: </span>
          {finding.problem}
          {finding.applied && (
            <span className="text-ready ml-2 font-semibold">
              {t("areaReview.applied")}
            </span>
          )}
        </p>
        <p className="text-muted italic">”{finding.claim}”</p>
        {finding.action === "replace" && finding.replace !== null && (
          <p>
            <span className="text-muted">{t("areaReview.becomes")} </span>”
            {finding.replace}”
          </p>
        )}
        {finding.action === "remove_producer" && finding.producer !== null && (
          <p>{t("areaReview.removeProducer", { producer: finding.producer })}</p>
        )}
        {finding.action === "set_grade" && finding.year !== null && (
          <p>
            {t("areaReview.setGrade", {
              year: finding.year,
              grade: finding.grade ?? "–",
            })}
            {finding.replace !== null && ` ”${finding.replace}”`}
          </p>
        )}
        {finding.action === "note" && (
          <p className="text-muted">{t("areaReview.byHand")}</p>
        )}
        {!finding.applied && finding.action !== "note" && !finding.applicable && (
          <p className="text-muted">{t("areaReview.cannotApply")}</p>
        )}
        {finding.source !== null && (
          <p>
            <a
              href={finding.source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent break-all underline-offset-4 hover:underline"
            >
              {finding.source.title}
            </a>
          </p>
        )}
      </div>
    </li>
  );
}

/** Every fix that can be made, ticked — the administrator unticks the ones
 *  they do not accept, which is the rarer act. */
function defaults(findings: readonly AreaFinding[] | undefined): ReadonlySet<string> {
  return new Set((findings ?? []).filter((f) => f.applicable).map((f) => f.id));
}
